import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  CheckCircle2,
  UserCheck,
  Recycle,
  Milk,
  Check,
  ShieldCheck
} from "lucide-react";
import urefill  from '@/assets/urefill.jpg'

const WaterRefillPage = () => {
  const values = [
    {
      icon: Recycle,
      title: "Affordable/Eco-Friendly",
      description: "Refill your own bottles and help reduce plastic waste while saving money.",
      features: [
        "Low-cost refills",
        "Bring your own containers",
        "Reduce single-use plastic",
      ],
    },
    {
      icon: UserCheck,
      title: "Easy Self-Serve Refill",
      description: "Our refill station is simple and convenient:",
      features: [
        "Bring your bottle or purchase in-store",
        "Fill up at the water station",
        "Pay quickly and get back to your day",
      ],
    },    
    {
      icon: ShieldCheck,
      title: "Quality you can trust",
      description: "Our advanced filtration system removes:",
      features: [
        "Chlorine and unpleasant odors",
        "Heavy metals (lead, mercury)",
        "Bacteria and contaminants",
        "Dissolved solids that affect taste",
      ],
    },
    {
      icon: Milk,
      title: "Perfect For",
      description: "",
      features: [
        "Drinking water at home",
        "Coffee & tea brewing",
        "Cooking and food prep",
        "Aquariums and pets",
        "Offices and small businesses",
      ],
    },
  ];

  const purificationSteps = [
    {
      title: "1. Pre-Filtration",
      description: "Water first passes through sediment and carbon filters to remove:",
      features: [
        "Dirt and particles",
        "Chlorine and odors",
        "Organic contaminants",
      ],
    },
    {
      title: "2. Reverse Osmosis Membrane",
      description: "The water is then pushed through a specialized membrane that removes:",
      features: [
        "Heavy metals (like lead and mercury)",
        "Dissolved salts and minerals",
        "Bacteria and microscopic impurities",
      ],
    },
    {
      title: "3. Final Polishing Filter",
      description: "Before dispensing, the water goes through a final carbon filter to:",
      features: [
        "Enhance taste",
        "Ensure freshness",
        "Deliver crisp, clean water",
      ],
    },
  ];

  return (
    <div className="pt-20">

      {/* Fresh Gear Section */}
      <section className="bg-gradient-to-br from-white via-sky-50 to-sky-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="block text-sky-600 font-medium text-sm uppercase tracking-wider text-center">Reverse Osmosis Water Refill</span>
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mt-3 text-center">
            Pure. Clean. Refreshing Water — Every Time.
          </h2>
          <p className="text-slate-600 mt-6 text-lg">
            Enjoy high-quality drinking water with our Reverse Osmosis refill station, designed to remove impurities and deliver fresh, great-tasting water you can trust.
          </p>
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>              
              <ul className="mt-8 space-y-4">
                {[
                  "Purified by Reverse Osmosis daily",
                  "Crisp, clean taste",
                  "Free Bottle Caps",
                  "Free Bottle Sanitizing",
                  "Safe for drinking and cooking",
                  "Better for your health and appliances",
                  "Water plans available",
                  "Good for the environment.",
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-sky-100 flex items-center justify-center">
                      <CheckCircle2 className="w-4 h-4 text-sky-600" />
                    </div>
                    <span className="text-slate-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="mt-8 relative">
              <img
                src={urefill}
                alt="Fresh Gear Ozone"
                className="rounded-2xl shadow-lg"
              />
            </div>
          </div>          
          <div className="mt-8 flex flex-wrap gap-4">
            <Link to="/contact">
              <Button className="bg-sky-600 hover:bg-sky-700 rounded-full px-8" data-testid="freshgear-book-btn">
                Refill your Water
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-sky-600 font-medium text-sm uppercase tracking-wider">Clean water. Better taste. Smarter choice</span>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mt-3">
              Why Choose Our RO Water? 
            </h2>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <Card key={index} className="border-slate-100 service-card" data-testid={`value-${index}`}>
                <CardHeader className="pb-4">
                  <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-4 ${
                    value.highlight 
                      ? "bg-gradient-to-br from-sky-500 to-sky-600 text-white" 
                      : "bg-sky-50 text-sky-600"
                  }`}>
                    <value.icon className="w-7 h-7" />
                  </div>
                  <CardTitle className="text-xl">{value.title}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-slate-600">{value.description}</p>
                  <ul className="space-y-2">
                    {value.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-slate-600">
                        <Check className="w-4 h-4 text-sky-500 mt-0.5 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Hero */}
      <section className="bg-gradient-to-br from-white via-sky-50 to-sky-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="griditems-center">
            <div className="text-center">
              <span className="text-sky-600 font-medium text-sm uppercase tracking-wider">How It Works</span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 mt-3">
                The Purified Water Difference
              </h1>
              <p>Reverse osmosis is one of the most effective water purification methods available giving you peace of mind with every refill.</p>
            </div>
            <div className="grid md:grid-cols-3 gap-8 mt-12">
              {purificationSteps.map((value, index) => (
                <Card key={index} className="border-slate-100 service-card" data-testid={`value-${index}`}>
                  <CardContent className="p-8 text-left">
                    <h3 className="text-xl font-semibold text-slate-900 mb-3">{value.title}</h3>
                    <p className="text-slate-600">{value.description}</p>
                    <ul className="space-y-2 mt-2">
                    {value.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-slate-600">
                        <Check className="w-4 h-4 text-sky-500 mt-0.5 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  </CardContent>
                </Card>
              ))}
            </div> 
            <div className="mt-12 text-left relative">
              <span className="text-sky-600 font-medium text-lg uppercase tracking-wider">How does Reverse Osmosis System work?</span>
              <iframe
                src="https://youtube.com/embed/djhhGwJb4VA"
                alt="Reverse Osmosis Purification"
                className="rounded-2xl shadow-lg min-h-24 md:min-h-80 max-w-64 md:max-w-[100%] md:min-w-[60%]"
                allow="autoplay; accelerometer; allowFullScreen"
              />
            </div>
          </div>
        </div>
      </section>     


      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-sky-600 to-sky-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white">
            Visit Us Today
          </h2>
          <p className="text-sky-100 mt-4 text-lg">
            Stop by and experience the difference of premium purified water at an affordable price.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link to="/contact">
              <Button size="lg" className="bg-white text-sky-700 hover:bg-sky-50 rounded-full px-8" data-testid="about-cta-book-btn">
                Get Directions
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WaterRefillPage;
